import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuthData } from '~/auth';
import {
    trackQuickNavClose,
    trackQuickNavNoResults,
    trackQuickNavOpen,
    trackQuickNavSelect,
} from './analytics';
import { matchItems } from './match';
import { findNavigablePath, getNavigablePages, resolvePagesByPaths } from './pages';
import QuickNavModal from './QuickNavModal';
import { getRecentPaths, pushRecentPath } from './recent';
import { SUGGESTED_PATHS } from './suggested';
import type {
    QuickNavCloseReason,
    QuickNavItem,
    QuickNavOpenSource,
    QuickNavSection,
    QuickNavSectionId,
} from './types';
import { useQuickNavShortcut } from './useQuickNavShortcut';

type QuickNavContextValue = {
    isAvailable: boolean;
    open: (source?: QuickNavOpenSource) => void;
};

const QuickNavContext = createContext<QuickNavContextValue>({
    isAvailable: false,
    open: () => {},
});

export function useQuickNav(): QuickNavContextValue {
    return useContext(QuickNavContext);
}

export function canUseQuickNavigation(user: { isRemotely: () => boolean }): boolean {
    return user.isRemotely();
}

export function QuickNavProviderStub({
    children,
    open,
    isAvailable = true,
}: {
    children: ReactNode;
    open: (source?: QuickNavOpenSource) => void;
    isAvailable?: boolean;
}) {
    const value = useMemo(() => ({ isAvailable, open }), [isAvailable, open]);
    return <QuickNavContext.Provider value={value}>{children}</QuickNavContext.Provider>;
}

export default function QuickNavProvider({ children }: { children: ReactNode }) {
    const { user } = useAuthData();
    const location = useLocation();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [query, setQuery] = useState('');
    const [recentPaths, setRecentPaths] = useState<string[]>([]);
    const closeReasonRef = useRef<QuickNavCloseReason>('outside');
    const trackedClose = useRef(false);
    const trackedNoResults = useRef<string | null>(null);
    const showRef = useRef(show);
    useEffect(() => {
        showRef.current = show;
    }, [show]);
    const isAvailable = canUseQuickNavigation(user);

    const pages = useMemo(() => (isAvailable ? getNavigablePages(user) : []), [isAvailable, user]);
    const allowedPaths = useMemo(() => new Set(pages.map((p) => p.path)), [pages]);

    const open = useCallback(
        (source: QuickNavOpenSource = 'icon') => {
            if (!isAvailable || showRef.current) return;

            trackedClose.current = false;
            closeReasonRef.current = 'outside';
            trackedNoResults.current = null;
            setQuery('');
            if (user.id) {
                setRecentPaths(getRecentPaths(user.id));
            }
            trackQuickNavOpen(user, source);
            setShow(true);
        },
        [isAvailable, user],
    );

    useQuickNavShortcut(
        useCallback(() => open('shortcut'), [open]),
        isAvailable,
    );

    useEffect(() => {
        if (!isAvailable || !user.id) return;
        const path = findNavigablePath(location.pathname, pages);
        if (!path) return;
        setRecentPaths(pushRecentPath(user.id, path, allowedPaths));
    }, [allowedPaths, isAvailable, location.pathname, pages, user.id]);

    const sections: QuickNavSection[] = useMemo(() => {
        const trimmed = query.trim();
        if (trimmed) {
            return [
                {
                    id: 'results' as const,
                    title: 'Results',
                    items: matchItems(pages, trimmed),
                },
            ];
        }

        const recent = resolvePagesByPaths(pages, recentPaths);
        const suggested = resolvePagesByPaths(pages, SUGGESTED_PATHS);

        return [
            ...(recent.length > 0
                ? [{ id: 'recent' as const, title: 'Recent', items: recent }]
                : []),
            ...(suggested.length > 0
                ? [{ id: 'suggested' as const, title: 'Suggested', items: suggested }]
                : []),
        ];
    }, [pages, query, recentPaths]);

    const handleClose = useCallback(() => {
        if (!trackedClose.current) {
            trackQuickNavClose(user, closeReasonRef.current);
            trackedClose.current = true;
        }
        setQuery('');
        trackedNoResults.current = null;
        setShow(false);
    }, [user]);

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value);
            const trimmed = value.trim();
            if (!trimmed) {
                trackedNoResults.current = null;
                return;
            }
            if (matchItems(pages, trimmed).length > 0) {
                trackedNoResults.current = null;
                return;
            }
            if (trackedNoResults.current === trimmed) return;
            trackedNoResults.current = trimmed;
            trackQuickNavNoResults(user, trimmed);
        },
        [pages, user],
    );

    const handleSelect = useCallback(
        (item: QuickNavItem, sectionId: QuickNavSectionId) => {
            if (user.id) {
                setRecentPaths(pushRecentPath(user.id, item.path, allowedPaths));
            }
            trackQuickNavSelect(user, {
                path: item.path,
                title: item.label,
                section: sectionId,
                query: query.trim() || undefined,
            });
            closeReasonRef.current = 'navigate';
            void navigate(item.path);
            handleClose();
        },
        [allowedPaths, handleClose, navigate, query, user],
    );

    const value = useMemo(
        () => ({
            isAvailable,
            open,
        }),
        [isAvailable, open],
    );

    return (
        <QuickNavContext.Provider value={value}>
            {children}
            {isAvailable && (
                <QuickNavModal
                    show={show}
                    sections={sections}
                    inputValue={query}
                    onInputChange={handleInputChange}
                    onSelect={handleSelect}
                    onClose={handleClose}
                />
            )}
        </QuickNavContext.Provider>
    );
}
